import React from 'react';
import { AlertTriangle, RotateCcw, Copy, Trash2 } from 'lucide-react';
import { ValidationError } from '../../utils/validation';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import * as matchStorage from '../../storage/matchStorage';

interface ErrorScreenProps {
  errors: ValidationError[];
  onRetry: () => void;
  /**
   * Paired tablet (kiosk): the invalid match was pushed to it and sits in its storage. Retry
   * would re-read it and loop, so it is hidden; clearing drops the match only and keeps the
   * pairing (reset.html would wipe the pairing too), back to the waiting screen.
   */
  paired?: boolean;
}

export function ErrorScreen({ errors, onRetry, paired = false }: ErrorScreenProps) {
  const { t } = useTranslation();

  const clearMatchAndReload = (event: React.MouseEvent) => {
    event.preventDefault();
    matchStorage.clearAll();
    window.location.href = `${process.env.PUBLIC_URL}/`;
  };
  
  const copyErrors = async () => {
    try {
      const errorText = errors.map(error => `${error.field}: ${error.message}`).join('\n');
      await navigator.clipboard.writeText(errorText);
      alert(t('screens.error.errorsCopied'));
    } catch (err) {
      console.error(t('screens.error.cannotCopy'), err);
    }
  };

  return (
    <div className="min-h-screen p-4 flex items-center justify-center">
      <div className="max-w-md mx-auto">
        {/* Header d'erreur */}
        <div className="bg-white p-6 text-center mb-4">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('screens.error.configError')}
          </h1>
          
          <p className="text-gray-600 text-sm">
            {t('screens.error.invalidData')}
          </p>
        </div>

        {/* Liste des erreurs */}
        <div className="bg-white p-4 mb-4">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <span className="bg-red-500 text-white text-xs px-2 py-1">
              {errors.length}
            </span>
            {t('screens.error.problemsDetected', { params: { count: errors.length } })}
          </h2>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {errors.map((error, index) => (
              <div key={index} className="border-l-4 border-red-400 bg-red-50 p-3">
                <div className="font-medium text-red-800 text-sm">
                  {error.field}
                </div>
                <div className="text-red-600 text-xs mt-1">
                  {error.message}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {!paired && (
            <button
              onClick={onRetry}
              className="w-full bg-blue-600 text-white py-3 font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <RotateCcw size={16} />
              {t('common.retry')}
            </button>
          )}
          
          <button
            onClick={copyErrors}
            className="w-full bg-gray-500 text-white py-3 font-semibold hover:bg-gray-600 flex items-center justify-center gap-2"
          >
            <Copy size={16} />
            {t('screens.error.copyErrors')}
          </button>

          <a
            href={`${process.env.PUBLIC_URL}/reset.html?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            onClick={paired ? clearMatchAndReload : undefined}
            className="w-full bg-orange-500 text-white py-3 font-semibold hover:bg-orange-600 flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            {t('screens.error.clearStorage')}
          </a>
        </div>

        {/* Aide */}
        <div className="bg-blue-50 border border-blue-200 p-4 mt-4">
          <h3 className="font-medium text-blue-900 mb-2">
            {t('screens.error.expectedFormat')}
          </h3>
          <div className="text-blue-800 text-xs space-y-1">
            <div>• Définir window.matchData avant le chargement</div>
            <div>• Format JSON avec tous les champs obligatoires</div>
            <div>• Date: "YYYY-MM-DD HH:MM"</div>
            <div>• URL callback: HTTPS uniquement</div>
          </div>
        </div>

        {/* Exemple de structure JSON */}
        <div className="bg-gray-50 border border-gray-200 p-4 mt-4">
          <h3 className="font-medium text-gray-900 mb-2 text-sm">
            {t('screens.error.validJsonExample')}
          </h3>
          <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">
{`{
  "matchId": "match_001",
  "competitionName": "Tournament Name",
  "categoryName": "Men Senior",
  "drawName": "Final",
  "refereeName": "John Referee",
  "dateTime": "2025-06-19 15:30",
  "courtName": "Court 1",
  "scoringType": "BestOf5",
  "playerA": "Player A Name",
  "playerB": "Player B Name",
  "resultCallbackUrl": "https://api.example.com/results",
  "redirectUrl": "https://example.com/tournament/results"
}`}
          </pre>
        </div>
      </div>
    </div>
  );
}